/* eslint-disable class-methods-use-this */
import EventClass from "common/utils/ui/event-class";

class MagnifierMouseEnterHandler extends EventClass {
  static DEFAULT_PREVIEW_HEIGHT = 600;

  #$imageBox = undefined;

  #config = undefined;

  #backdrop = undefined;

  get #$original() {
    return this.#$imageBox.querySelector(".js-magnify-image");
  }

  get #$magnified() {
    return this.#$imageBox.querySelector(".js-magnify-preview");
  }

  get #$lens() {
    return this.#$imageBox.querySelector(".js-magnify-lens");
  }

  get #isLeftSided() {
    return this.#config.position === "left";
  }

  get #backgroundColor() {
    const { backgroundColor } = this.#$magnified.dataset;
    return backgroundColor;
  }

  get #isTransparentBackground() {
    return this.#backgroundColor === undefined || this.#backgroundColor === "transparent";
  }

  get #useFixedPositioning() {
    return this.#config.useFixedPositioning === true;
  }

  get #$panel() {
    if (this.#isLeftSided) {
      return document.querySelector(".js-info-left");
    }
    return document.querySelector(".js-info-right");
  }

  constructor({ event, eventSelector, config }) {
    super({ eventSelector, event });
    this.#$imageBox = document.querySelector(eventSelector);
    this.#config = config || {};
  }

  #setBackgroundImage() {
    const $magnified = this.#$magnified;
    // eslint-disable-next-line no-unused-vars
    const [imageSrc, transparentBg] = getComputedStyle($magnified).backgroundImage.split(", ");
    if (this.#isTransparentBackground) {
      this.#backdrop = transparentBg ?? this.#backdrop;
      this.#$magnified.style.backgroundImage = `url("${this.#$original.src}"), ${this.#backdrop}`;
      this.#$magnified.style.backgroundRepeat = "no-repeat, repeat";
      return;
    }
    this.#$magnified.style.backgroundImage = `url("${this.#$original.src}")`;
    this.#$magnified.style.backgroundColor = this.#backgroundColor;
  }

  /**
   * @returns void
   * @description Set up magnified image width based on panel dimensions
   */
  #setupMagnifiedWidth(imageBox) {
    if (this.#$panel && !this.#config.isModal) {
      const panelRect = this.#$panel.getBoundingClientRect();
      this.#$magnified.style.width = this.#isLeftSided
        ? `${panelRect.width}px`
        : `${panelRect.right - imageBox.right - 10}px`;
    }
  }

  /**
   * @returns void
   * @description Apply cursor styles to relevant elements
   */
  #applyCursorStyles() {
    this.#$original.style.cursor = "pointer";
    if (this.#$imageBox) this.#$imageBox.style.cursor = "pointer";
    this.#$lens.style.cursor = "pointer";
  }

  /**
   * @returns void
   * @description Set up fixed positioning for magnified preview
   */
  #setupFixedPositioning(imageBox) {
    const viewportHeight = window.innerHeight;
    let previewHeight = this.#$magnified.getBoundingClientRect().height;
    if (!previewHeight) previewHeight = MagnifierMouseEnterHandler.DEFAULT_PREVIEW_HEIGHT;

    this.#$magnified.style.position = "fixed";
    this.#$magnified.style.right = "auto";

    // Calculate vertical position to center the preview in viewport
    const centerY = (viewportHeight - previewHeight) / 2;
    this.#$magnified.style.top = `${Math.max(10, centerY)}px`;

    // Set horizontal position based on side
    this.#$magnified.style.left = this.#isLeftSided
      ? `${Math.max(10, imageBox.left - 540)}px`
      : `${imageBox.right + 10}px`;
  }

  /**
   * @returns void
   * @description Set up absolute positioning for magnified preview
   */
  #setupAbsolutePositioning() {
    this.#$magnified.style.position = "absolute";
    this.#$magnified.style.top = "-2px";

    if (this.#isLeftSided) {
      this.#$magnified.style.right = "calc(100% + 10px)";
      this.#$magnified.style.left = "auto";
    } else {
      this.#$magnified.style.left = `${this.#$original.offsetWidth + 10}px`;
      this.#$magnified.style.right = "auto";
    }
  }

  /**
   * @returns void
   * @override EventClass.event
   * @description This method is called when the mouse moved into the image.
   */
  event() {
    if (!this.#$original) return;

    const imageBox = this.#$original.getBoundingClientRect();

    this.#setupMagnifiedWidth(imageBox);
    this.#applyCursorStyles();
    this.#$magnified.style.display = "block";

    if (this.#useFixedPositioning) {
      this.#setupFixedPositioning(imageBox);
    } else {
      this.#setupAbsolutePositioning();
    }

    this.#$lens.style.display = "block";
    this.#setBackgroundImage();
  }
}

export default props => new MagnifierMouseEnterHandler({ event: "mouseenter", ...props });
